import React, { useCallback, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";

import { deleteCard } from "../../../../services/apiCards";
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import { useResponsive } from "../../../../hooks/useResponsive";
import { Tooltip, Typography } from "@mui/material";
import { TooltipName } from "../../../../utils/Enums";
import { DeleteIcone } from "../../../../components/UI/Icons";
import FormDialog from "../../../../components/Dialog/FormDialog";
import useMutate from "../../../../hooks/useMutate";

const DeleteCard = ({
  cardId,
  boardId,
  kanbanCardDelete,
  setShowDetails,
  className = "",
  DeleteIconStyle = "",
}) => {
  const [showDelete, setShowDelete] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { id } = useParams();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const isMobile = useResponsive("down", "md");
  const queryClient = useQueryClient();

  const handleDeleteSuccess = useCallback(() => {
    const nextSearchParams = new URLSearchParams(searchParams);
    nextSearchParams.delete("card_id");
    nextSearchParams.delete("brd_id");
    const nextSearch = nextSearchParams.toString();

    queryClient.invalidateQueries({
      queryKey: ['cards', boardId],
    });
    setLoading(false);
    setShowDelete(false);

    if (typeof setShowDetails === "function") {
      setShowDetails(false);
    }

    if (id) {
      navigate(
        {
          pathname: location.pathname.includes("/feeds/")
            ? `/feeds/${id}`
            : `/board/${id}`,
          search: nextSearch ? `?${nextSearch}` : "",
        },
        { replace: true }
      );
      return;
    }

    navigate(
      {
        pathname: location.pathname,
        search: nextSearch ? `?${nextSearch}` : "",
      },
      { replace: true }
    );
  }, [
    boardId,
    id,
    location.pathname,
    navigate,
    queryClient,
    searchParams,
    setShowDetails,
  ]);

  const { mutate: deleteCardMutate } = useMutate(deleteCard,
    ['mineCards', 'watching'],
    ()=>{},
    handleDeleteSuccess,
    () => {
      setLoading(false);
      setShowDelete(false);
    },
    () => {
      setLoading(false);
      setShowDelete(false);
    },
  );

  const callback = useCallback((e) => {
    setLoading(true);
    deleteCardMutate(cardId);
  }, [cardId, deleteCardMutate]);

  const handleCardDelete = useCallback((e) => {
    e.stopPropagation();
    setShowDelete(true);
  }, []);

  const handleClose = useCallback((e) => {
    e.stopPropagation();
    setShowDelete(false);
  }, []);

  return (
    <span className={`d-flex align-items-center  ${className}`}>
      {showDelete && (
        <FormDialog
          open={showDelete}
          handleClose={handleClose}
          loading={loading}
          handleSubmit={callback}
          buttonTxt="Delete"
          title="Confirmation: Delete Task"
          alignActions="center"
          cancelBtn={true}
          fullWidth={true}>
          <Typography className="align_text_center mb-2">
            Are you sure you want to delete this item? This action cannot be
            undone.
          </Typography>
        </FormDialog>
      )}
      {isMobile ? (
        <>
          <Typography
            className={"workflow_steps d-flex align-items-center delete-card"}
            onClick={(e) => {
              e.stopPropagation();
              handleCardDelete(e);
            }}>
            <DeleteIcone className={"delete-card-button mr-2"} />
            Delete Card
          </Typography>
        </>
      ) : (
        <Tooltip title={TooltipName.KANBAN_CARD.DELETE_THIS_TASK}>
          <div
            onClick={(e) => {
              e.stopPropagation();
              handleCardDelete(e);
            }}
            className={kanbanCardDelete ? "delete-card" : "delete-card-button"}>
            <DeleteIcone className={DeleteIconStyle} />
          </div>
        </Tooltip>
      )}
    </span>
  );
};

export default React.memo(DeleteCard);
